import React, {useMemo} from 'react';
import {t} from 'i18next';
import {Box} from '@mui/material';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import ActionMenuWrapper from '../../../../components/action-menu/action-menu-wrapper/ActionMenuWrapper';
import {CustomMenuItem} from '../../../../components/common/arr-menu/ArrMenu';
import './SidePanelConceptExp.scss';
import {
  RoleId,
  ReportActionMenuOptionTypes,
  SourceTypeCode,
} from '../../../../shared/constants';
import {IChild} from '../heated-map-panel/TypesHeatMap';

interface ISidePanelConceptExpProps {
  cartItems: IChild[];
  onMenuAction?: (actionId: string) => void;
  onActionSelect?: (actionId: string) => void;
  sourceDetails?: {[key: string]: any};
  getDoActionData?: (data: {
    operationType?: string;
    sourceDetails?: {[key: string]: any};
  }) => {selectedList?: object; destinationList?: object};
  getSourceDetails?: (
    sourceDetails: {[key: string]: any},
    forAPI?: string
  ) => {
    [key: string]: any;
  };
  showToastFromParent?: (config: any) => void;
  onActionPick?: (
    action: CustomMenuItem,
    callBackForDoc?: (docId: string, source: string) => void
  ) => void;
  validateBeforeBulkAction?: () => boolean;
}

const SidePanelConceptExp = ({
  cartItems = [],
  onActionSelect,
  sourceDetails,
  getDoActionData,
  getSourceDetails,
  showToastFromParent,
  onActionPick,
  validateBeforeBulkAction,
}: ISidePanelConceptExpProps) => {
  const roleId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.roleId
  ); // PAU = rollId 1, RMU = roleId 2

  const handleActionClick = (selectedItem: CustomMenuItem) => {
    if (onActionSelect) {
      onActionSelect(selectedItem.id);
    }
  };

  const actionMenuItems: CustomMenuItem[] = useMemo(() => {
    const commonItems: CustomMenuItem[] = [
      {
        id: ReportActionMenuOptionTypes.View,
        label: t(
          'reports.dataAnalysisReport.conceptExplorer.SidePanel.actionMenuItems.view'
        ),
        submenu: [
          {
            id: ReportActionMenuOptionTypes.ViewInDocView,
            label: t(
              'reports.dataAnalysisReport.conceptExplorer.SidePanel.actionMenuItems.viewInDocView'
            ),
          },
          {
            id: ReportActionMenuOptionTypes.ViewInDocViewNewTab,
            label: t(
              'reports.dataAnalysisReport.conceptExplorer.SidePanel.actionMenuItems.viewInDocViewInNewTab'
            ),
          },
          {
            id: ReportActionMenuOptionTypes.ViewInDocList,
            label: t(
              'reports.dataAnalysisReport.conceptExplorer.SidePanel.actionMenuItems.viewInDocList'
            ),
          },
        ],
      },
      {
        id: ReportActionMenuOptionTypes.BulkTag,
        label: t(
          'reports.dataAnalysisReport.conceptExplorer.SidePanel.actionMenuItems.bulkTag'
        ),
      },
      {
        id: ReportActionMenuOptionTypes.BulkFolder,
        label: t(
          'reports.dataAnalysisReport.conceptExplorer.SidePanel.actionMenuItems.bulkFolder'
        ),
      },
    ];

    const roleSpecificItem: CustomMenuItem | null =
      roleId?.toString() === RoleId.ReviewManager
        ? {
            id: ReportActionMenuOptionTypes.BulkAssign,
            label: t(
              'reports.dataAnalysisReport.conceptExplorer.SidePanel.actionMenuItems.bulkAssign'
            ),
          }
        : roleId?.toString() === RoleId.ProjectAdministrator
          ? {
              id: ReportActionMenuOptionTypes.BulkRelease,
              label: t(
                'reports.dataAnalysisReport.conceptExplorer.SidePanel.actionMenuItems.bulkRelease'
              ),
            }
          : null;

    const bulkActionItems = roleSpecificItem
      ? [...commonItems, roleSpecificItem]
      : commonItems;

    // Add custom actions (tally, comm_explorer) that are not handled by ActionMenuWrapper
    return [
      ...bulkActionItems,
      {
        id: ReportActionMenuOptionTypes.Tally,
        label: t(
          'reports.dataAnalysisReport.conceptExplorer.SidePanel.actionMenuItems.tally'
        ),
      },
      {
        id: 'comm_explorer',
        label: t(
          'reports.dataAnalysisReport.conceptExplorer.SidePanel.actionMenuItems.commExplorer'
        ),
      },
    ];
  }, [roleId, t]);

  // Handle custom actions (comm_explorer) that ActionMenuWrapper doesn't handle
  // Note: Navigation actions (ViewInDocList, ViewInDocView, ViewInDocViewNewTab) and Tally
  // are now handled by ActionMenuWrapper, so we don't need to handle them here
  const handleCustomAction = (action: CustomMenuItem) => {
    if (action.id === 'comm_explorer') {
      handleActionClick(action);
    }
    // Navigation actions and Tally are handled by ActionMenuWrapper via onActionPick callback
  };

  // Check if sourceDetails is valid (not empty/null)
  const hasValidSourceDetails = useMemo(() => {
    return (
      sourceDetails &&
      typeof sourceDetails === 'object' &&
      Object.keys(sourceDetails).length > 0
    );
  }, [sourceDetails]);

  // Determine if the menu should be disabled
  // Original behavior: menu is disabled when cart is empty
  // Note: validateBeforeBulkAction should NOT be called here as it shows error messages
  // It should only be called when an action is actually selected (handled by ActionMenuWrapper)
  const isMenuDisabled = useMemo(() => {
    // Disable if cart is empty (original behavior)
    return cartItems.length <= 0;
  }, [cartItems.length]);

  // Ensure sourceDetails is always an object (not null/undefined) for ActionMenuWrapper
  // ActionMenuWrapper will handle empty sourceDetails gracefully for bulk actions
  const safeSourceDetails = useMemo(() => {
    return hasValidSourceDetails ? sourceDetails : {};
  }, [hasValidSourceDetails, sourceDetails]);

  return (
    <Box className={'action-area'}>
      <ActionMenuWrapper
        actions={actionMenuItems}
        sourceType={SourceTypeCode.CONCEPTEXPLORER}
        sourceTypeContext="CONCEPTEXPLORERWC"
        sourceDetails={safeSourceDetails}
        getDoActionData={getDoActionData}
        getSourceDetails={getSourceDetails}
        showToastFromParent={showToastFromParent}
        onActionPick={(action, callback) => {
          // Handle custom action (comm_explorer) that doesn't need validation
          if (action.id === 'comm_explorer') {
            handleCustomAction(action);
            return;
          }

          // Tally is now handled by ActionMenuWrapper - just pass through
          // ActionMenuWrapper will provide the callback with tally signature
          if (
            action.id === ReportActionMenuOptionTypes.Tally ||
            action.id === 'tally'
          ) {
            // Pass through to parent - ActionMenuWrapper will handle tally and call callback
            if (onActionPick) {
              onActionPick(action, callback);
            }
            return;
          }

          // For bulk actions, validate before proceeding
          // Only validate for bulk actions, not for navigation actions
          const bulkActionIds = [
            ReportActionMenuOptionTypes.BulkTag,
            ReportActionMenuOptionTypes.BulkFolder,
            ReportActionMenuOptionTypes.BulkAssign,
            ReportActionMenuOptionTypes.BulkRelease,
          ];

          if (
            bulkActionIds.includes(action.id as ReportActionMenuOptionTypes)
          ) {
            // Validate before allowing bulk action
            if (validateBeforeBulkAction && !validateBeforeBulkAction()) {
              // Validation failed, error toast already shown by validateBeforeBulkAction
              return;
            }
          }

          // Call parent's onActionPick
          // ActionMenuWrapper will handle navigation actions (ViewInDocList, ViewInDocView, etc.)
          // and call this callback with docId when ready
          if (onActionPick) {
            onActionPick(action, callback);
          }
        }}
        isDisabled={isMenuDisabled}
        size="small"
        subMenuDirection="left"
        direction="bottom"
        dataTestId="concept-explorer-action-menu"
        additionalParameters={
          safeSourceDetails && cartItems.length > 0
            ? {
                documentCount: cartItems
                  .reduce((sum, item) => sum + item.size, 0)
                  .toString(),
              }
            : {}
        }
      />
    </Box>
  );
};

export default SidePanelConceptExp;
